import { ApplicationScope, CustomerShort, Organisation } from "~/api-client";
import { defineStore } from "pinia";
import { errorMsg } from "~/modules/common/services/util/util";

interface State {
  _allOrganisations: Organisation[];
  _currentOrganisation: Organisation;

}

export const useOrganisationStore = defineStore('organisation', {
  state: (): State => ({
    _allOrganisations: [],
    _currentOrganisation: {
      code: "",
      name: ""
    },
  }),

  getters: {
    allOrganisations: (state) => (state._allOrganisations),
    allActiveOrganisations: (state) => (state._allOrganisations.filter(group => group.readOnly == undefined || !group.readOnly)),
    allDeactivatedOrganisations: (state) => (state._allOrganisations.filter(group => group.readOnly == true)),
    currentOrganisation: (state) => state._currentOrganisation,
  },
  actions: {
    async getAllOrganisations(): Promise<Organisation[]>{
      const {$backendService, $logger} = useNuxtApp();

      return $backendService.organisationControllerApi.getAllOrganisations({xApplicationScope: ApplicationScope.MyDb})
        .then((result) => {
          if (!result) {
            throw new Error("unable to load organisations");
          }
          const newResult = result.map(orga => {
            if (!orga.acceptedPaymentMethods) {
              orga.acceptedPaymentMethods = ["PAYPAL"];
            }
            return orga;
          });
          this.$state._allOrganisations = newResult;
          $logger.info("loaded organisations");
          return newResult;
        }).catch((error) => {
          $logger.error("error fetching organisations", error);
          return [];
        });
    },
    getCurrentOrganisation(){
      const {$backendService, $logger} = useNuxtApp();

      return $backendService.organisationControllerApi.getCurrentOrganisation({xApplicationScope: ApplicationScope.MyDb})
        .then((orga) => {
          this.$state._currentOrganisation = orga;
          return orga;
        }).catch((error) => {
          $logger.error('error fetching current organisation')
          throw error;
        });
    },
    updateCurrentOrganisation(orgaToBeUpdated: Organisation){
      const {$backendService, $logger, $tenant} = useNuxtApp();

      return $backendService.organisationControllerApi.updateCurrentOrganisation({
        xApplicationScope: $tenant.getTenant,
        organisation: orgaToBeUpdated
      }).then(result=> {
        this.$state._currentOrganisation = result;
      }).catch((error) => {
        $logger.error('CHANGE_ORGA_MAIL', error);
        ElMessage.error('Leider ist ein Fehler aufgetreten.');
        throw error;
      });
    },
    createOrganisation(orga: Organisation) {
      const {$backendService, $logger} = useNuxtApp();
      return $backendService.organisationControllerApi.createOrganisation({
        xApplicationScope: ApplicationScope.MyDb,
        organisation: {
          code: orga.code.toUpperCase(),
          name: orga.name,
          baseUrl: orga.baseUrl,
          adminEmail: orga.adminEmail,
          enterpriseMode: orga.enterpriseMode
        }
      }).catch(error => {
        $logger.error('CREATE_ORGA', error);
        ElMessage.error('Leider ist ein Fehler aufgetreten.');
        throw error;
        });
    },
    updateOrganisation(orga: Organisation){
      const {$backendService, $logger} = useNuxtApp();

      return $backendService.organisationControllerApi.updateOrganisation({
        xApplicationScope: ApplicationScope.MyDb,
        id: orga.id!,
        organisation: {
          id: orga.id,
          code: orga.code.toUpperCase(),
          name: orga.name,
          baseUrl: orga.baseUrl,
          adminEmail: orga.adminEmail,
          enterpriseMode: orga.enterpriseMode
        }
      }).catch((error) => {
        $logger.error('CHANGE_ORGA_DATA', error);
        ElMessage.error('Leider ist ein Fehler aufgetreten.');
        throw error;
      });
    },
    deleteOrganisation(organisation: Organisation){
      const {$backendService, $logger} = useNuxtApp();

      return $backendService.organisationControllerApi.deleteOrganisation({
        xApplicationScope: ApplicationScope.MyDb,
        id: organisation.id!
      }).catch((error) => {
        $logger.error('DELETE_ORGA', error);
        ElMessage.error('Leider ist ein Fehler aufgetreten.');
        throw error;
      });
    },
    updateOrganisationPaymentType(organisation: Organisation){
      const {$backendService, $logger} = useNuxtApp();
      return $backendService.organisationControllerApi.updateOrganisationPaymentMethods({
        xApplicationScope: ApplicationScope.MyDb,
        id: organisation.id!,
        acceptedPaymentMethods: organisation.acceptedPaymentMethods,
      }).catch((error) => {
        $logger.error('UPDATE_ORGA_PAYMENT_TYPE', error);
        errorMsg(error).then(err => ElMessage.error("Leider ist ein Fehler aufgetreten. " + (!!err ? "- "+ err: "")));
        throw error;
      });
    },
    createOrganisationInAllOrganisationsState(orga: Organisation){
      this.$state._allOrganisations.push(orga);
    },
    updateOrganisationInAllOrganisationsState(orgaId: string, organisation: Organisation | null){
      if (!!organisation){
        this.$state._allOrganisations = this.$state._allOrganisations.map(orga => {
          if (orga.id === orgaId){
            return organisation!;
          }
          return orga;
        })
      } else {
        this.$state._allOrganisations = this.$state._allOrganisations.reduce((filtered: Organisation[], orga: Organisation) => {
          if (orga.id !== orgaId) {
            filtered.push(orga);
          }
          return filtered;
        }, []);
      }
    }
  }
})